import { useMemo } from "react"

import type { normalizeConversationTransportRow } from "@/utils/conversationsTransportRows"

import type { RecentSimulationItem } from "../types"

type RecentRow = ReturnType<typeof normalizeConversationTransportRow>

export function useRecentSimulations({
  items,
  metricId,
  isTraceMetric,
  importedSimulations,
  excludedIds,
  scopedSimulationIds,
}: {
  items: RecentRow[] | undefined
  metricId: string | undefined
  isTraceMetric: boolean
  importedSimulations: RecentSimulationItem[]
  excludedIds: Set<string>
  scopedSimulationIds: string[] | undefined
}): RecentSimulationItem[] {
  return useMemo(() => {
    const scopedIds = scopedSimulationIds ? new Set(scopedSimulationIds) : null
    const fetched: RecentSimulationItem[] = (items ?? [])
      .filter(
        (item): item is RecentRow & { simulation_output_id: string } =>
          item.simulation_output_id !== null,
      )
      .filter((item) => !excludedIds.has(item.simulation_output_id))
      .filter((item) => !scopedIds || scopedIds.has(item.simulation_output_id))
      .map((item) => ({
        id: item.simulation_output_id,
        name: item.simulation_output_id.slice(0, 8),
        description: item.agent_id || "Unknown agent",
        createdAt: item.created_at,
        agentId: item.agent_id ?? undefined,
        isImported: false,
        metricValue: metricId ? item.metricsById[metricId] : undefined,
        metricIsValues:
          isTraceMetric &&
          !!metricId &&
          item.metricOutputTypesById[metricId] === "set",
      }))
    const fetchedIds = new Set(fetched.map((item) => item.id))
    const imported = importedSimulations.filter(
      (item) =>
        !fetchedIds.has(item.id) &&
        !excludedIds.has(item.id) &&
        (!scopedIds || scopedIds.has(item.id)),
    )
    const knownIds = new Set([
      ...fetchedIds,
      ...imported.map((item) => item.id),
    ])
    const placeholders: RecentSimulationItem[] = (scopedSimulationIds ?? [])
      .filter((id) => !knownIds.has(id) && !excludedIds.has(id))
      .map((id) => ({
        id,
        name: id.slice(0, 8),
        description: "Test case simulation",
        createdAt: "",
        isImported: false,
      }))
    return [...imported, ...fetched, ...placeholders]
  }, [
    items,
    metricId,
    isTraceMetric,
    importedSimulations,
    excludedIds,
    scopedSimulationIds,
  ])
}
